<template>
    <div class="pm-modal" role="dialog" :aria-label="title">
        <InfoPopup v-model="showInfoPopup" :title="infoTitle" :showCancelButton="showInfoPopupCancelButton"
            @confirm="onConfirm">
            <p>{{ infoMessage }}</p>
        </InfoPopup>
        <InputPopup v-model="showEditForm" title="Edit Voice" :fields="editFields" @confirm="onConfirmEdit"
            @cancel="onCancelEdit" />
        <header class="pm-header">
            <div>
                <span class="pm-eyebrow">Personal library</span>
                <h3>{{ title }}</h3>
            </div>
            <span class="pm-count">{{ filtered.length }} voices</span>
        </header>

        <label class="pm-search-wrap">
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <input class="pm-search" type="text" v-model="q" :placeholder="placeholder" autofocus />
        </label>

        <div class="pm-gallery-toggle">
            <label class="switch">
                <input type="checkbox" v-model="onlyMyGallery" />
                <span class="slider"></span>
                <span class="switch-label">
                    Only My Gallery
                </span>
            </label>
        </div>
        <div class="pm-list">
            <div v-for="item in filtered" :key="itemKey(item)" class="pm-item vc-card"
                :class="{ playing: isPlaying(item) }" role="button" tabindex="0" @click="play(item)"
                @keydown.enter="play(item)">
                <div class="vc-avatar" :class="{ disabled: isLoading(item) }">
                    <!-- Play/Stop button inside avatar -->
                    <button class="vc-play-btn" type="button" @click.stop="isPlaying(item) ? stop(item) : play(item)"
                        :aria-label="isPlaying(item) ? 'Stop voice preview' : 'Play voice preview'">
                        <i v-if="!isPlaying(item)" class="fa-solid fa-play" aria-hidden="true"></i>
                        <i v-else class="fa-solid fa-stop" aria-hidden="true"></i>
                    </button>
                    <span v-if="isLoading(item)" class="spinner"></span>
                    <div class="vc-card-progress" v-if="isPlaying(item)"
                        :style="{ '--progress': Number(audioProgress[itemKey(item)]) ?? 0 }"></div>
                </div>
                <div class="vc-content">
                    <div class="vc-header">
                        <div class="vc-name-tags">
                            <div class="vc-name-row">
                                <span class="vc-designed-badge">Designed</span>
                                <h3>{{ displayName(item) }}</h3>
                            </div>
                            <div class="vc-tags">
                                <span v-if="item.tag">
                                    {{ displayTags(item) }}
                                </span>
                            </div>
                            <p class="vc-instruct">{{ displayInstrctText(item) }}</p>
                        </div>
                    </div>
                </div>
                <div class="vc-btn-group">
                    <!-- Like/Fav button -->
                    <button class="vc-fav-btn" type="button" :class="{ active: isInGallery(item) }"
                        @click.stop="toggleMyGallery(item)"
                        :aria-label="isInGallery(item) ? 'Remove from My Gallery' : 'Add to My Gallery'">
                        <i :class="isInGallery(item) ? 'fa-solid fa-heart' : 'fa-regular fa-heart'"
                            aria-hidden="true"></i>
                    </button>

                    <!-- More button dropdown -->
                    <el-dropdown @command="onDropdownAction(item, $event)" trigger="click">
                        <button class="vc-more-btn" aria-label="More actions" @click.stop>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="4" cy="10" r="2" fill="#888" />
                                <circle cx="10" cy="10" r="2" fill="#888" />
                                <circle cx="16" cy="10" r="2" fill="#888" />
                            </svg>
                        </button>
                        <template #dropdown>
                            <el-dropdown-menu>
                                <el-dropdown-item command="modify">
                                    <i class="fa-solid fa-pen-to-square" aria-hidden="true"></i>
                                    Modify
                                </el-dropdown-item>
                                <el-dropdown-item command="delete">
                                    <i class="fa-solid fa-trash-can" aria-hidden="true"></i>
                                    Delete
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </div>
            </div>
            <div v-if="filtered.length === 0" class="pm-empty">
                <i class="fa-regular fa-circle-question" aria-hidden="true"></i>
                <strong>No designed voices found.</strong>
                <span>Try another search, clear My Gallery, or create a new designed voice.</span>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, computed, reactive, ref, onMounted } from 'vue';
import { patchCollectVoice, deleteCollectVoice, getCollectVoiceIds } from '@/api/myGallery'
import { DeleteUserVoice, getUserVoicesByUserId, patchUserVoiceName, patchUserVoiceTag, patchUserVoiceInstructText } from '@/api/services'
import { getDownloadFileUrl } from '@/api/files';
import InfoPopup from '@/components/common/InfoPopup.vue';
import InputPopup from '@/components/common/InputPopup.vue';
import { ElDropdown, ElDropdownMenu, ElDropdownItem } from 'element-plus';
import { checkInput } from '@/utils/InputChecker';

export default defineComponent({
    name: 'VGUserVoiceExplorerModal',
    props: {
        title: { type: String, default: 'Select' },
        placeholder: { type: String, default: 'Search…' },
        labelKey: { type: String, default: 'label' },
        recommendationIds: { type: Array, default: () => [] },
        searchText: { type: String, default: '' },
    },
    components: {
        InfoPopup,
        InputPopup,
        ElDropdown,
        ElDropdownMenu,
        ElDropdownItem,
    },
    emits: ['select', 'update:searchText'],
    setup(props, { emit }) {
        const showInfoPopup = ref(false);
        const infoTitle = ref('');
        const infoMessage = ref('');
        const nextAction = ref('');
        const showInfoPopupCancelButton = ref(true);

        // For InputPopup edit form
        const showEditForm = ref(false);
        const editItem = ref(null);
        const editFields = ref([]);

        const userVoices = ref([]);

        const q = computed({
            get: () => props.searchText,
            set: (val) => emit('update:searchText', val)
        });
        const voiceImageUrls = reactive({});
        const onlyMyGallery = ref(false);
        const myGalleryIds = ref([]);

        const currentAudio = ref(null);

        const currentPlayingId = ref(null);

        const itemKey = it => it.id ?? it[props.labelKey] ?? JSON.stringify(it);
        const deleteTarget = ref(null);

        const loadingVoices = ref(new Set());
        const searchFields = ['voice_name', 'tag']

        const audioProgress = reactive({});

        const filtered = computed(() => {
            const raw = q.value || '';
            // Split by "||" and trim; ignore empty pieces
            const terms = raw
                .split('||')
                .map(t => t.trim().toLowerCase())
                .filter(Boolean);
            let base = userVoices.value;

            // If toggled, restrict to My Gallery voices
            if (onlyMyGallery.value) {
                base = base.filter(it => {
                    const id = it.voice_id ?? it.id ?? it.voiceId ?? '';
                    return myGalleryIds.value.includes(String(id));
                });
            }

            // Text search (OR across terms, OR across fields)
            if (terms.length) {
                base = base.filter(it =>
                    terms.some(term =>
                        searchFields.some(k => {
                            const v = (it?.[k] ?? '').toString().toLowerCase();
                            return v.includes(term);
                        })
                    )
                );
            }
            return base;
        });


        function isPlaying(item) {
            return currentPlayingId.value === voiceKey(item);
        }

        function stop() {
            if (currentAudio.value) {
                currentAudio.value.pause();
                currentAudio.value = null;
                currentPlayingId.value = null;
            }
        }

        async function loadVoices() {
            try {
                userVoices.value = await getUserVoicesByUserId();
            } catch (err) {
                console.error('Failed to load voices:', err);
            }
        }

        function displayName(item) {
            const raw = item.voice_name ?? item[props.labelKey] ?? item.name ?? item.id ?? '';
            return raw.length > 10 ? raw.slice(0, 10) + '...' : raw;
        }
        function displayTags(item) {
            const tag = item.tag;
            const tags = Array.isArray(tag)
                ? tag
                : (typeof tag === 'string' && tag.length ? tag.split(',').map(t => t.trim()) : []);
            let result = [];
            let totalLen = 0;
            for (let i = 0; i < tags.length; i++) {
                const t = tags[i];
                // Calculate what the length would be if we add this tag (including comma and space if not first)
                const addLen = (result.length > 0 ? 2 : 0) + t.length;
                if (totalLen + addLen > 20) {
                    result.push('...');
                    break;
                }
                result.push(t);
                totalLen += addLen;
            }
            return result.join(', ');
        }
        function displayInstrctText(item) {
            const text = item.instruct_text ?? '';
            if (!text.trim()) {
                return 'The text prompt is empty';
            }
            return text.length > 30 ? text.slice(0, 30) + '...' : text;
        }

        function select(item) {
            if (currentAudio.value) {
                currentAudio.value.pause();
                currentAudio.value = null;
            }
            emit('select', item);
            q.value = '';
        }

        async function play(item) {
            // stop any existing playback
            if (currentAudio.value) {
                stop(item);
                return;
            }
            currentPlayingId.value = voiceKey(item);
            select(item);
            const key = voiceKey(item);
            loadingVoices.value.add(key);

            const base_audio_id = item.audio_file_id
            const cleanAudioId = typeof base_audio_id === 'string'
                ? base_audio_id.replace(/^"|"$/g, '')
                : base_audio_id;
            try {
                const data = await getDownloadFileUrl(cleanAudioId);
                const audio = new Audio(data.url);
                currentAudio.value = audio;
                audio.play();
                audio.ontimeupdate = () => {
                    audioProgress[itemKey(item)] = audio.currentTime / audio.duration;
                };
                audio.onended = () => {
                    audioProgress[itemKey(item)] = 1;
                    currentAudio.value = null;
                    currentPlayingId.value = null;
                };
                console.log('Play voice:', item.voice_name);
            } catch (err) {
                nextAction.value = '';
                infoTitle.value = 'Error!';
                infoMessage.value = err;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return
            } finally {
                loadingVoices.value.delete(key);
            }
        }

        function voiceKey(item) {
            return item.voice_name ?? item[props.labelKey] ?? item.id ?? '';
        }
        function isLoading(item) {
            return loadingVoices.value.has(voiceKey(item));
        }


        function isInGallery(item) {
            const id = item.voice_id ?? item.id ?? item.voiceId ?? '';
            return myGalleryIds.value.includes(String(id));
        }

        async function toggleMyGallery(item) {
            const id = item.voice_id ?? item.id ?? item.voiceId ?? '';
            if (!id) {
                console.warn('toggleMyGallery: no voice_id on item', item);
                return;
            }

            const prev = [...myGalleryIds.value];
            const wasIn = myGalleryIds.value.includes(String(id));

            myGalleryIds.value = wasIn
                ? prev.filter(v => v !== id)
                : [...prev, id];

            try {
                if (wasIn) {
                    await deleteCollectVoice(id);
                } else {
                    await patchCollectVoice(id);
                }
            } catch (e) {
                // rollback on failure
                myGalleryIds.value = prev;
                console.error('toggleMyGallery failed', e);
            } finally {
                // background sync to ensure consistency
                refreshMyGallery().catch(() => { });
            }
        }

        async function refreshMyGallery() {
            try {
                const ids = await getCollectVoiceIds();
                myGalleryIds.value = Array.isArray(ids) ? ids : [];
            } catch (e) {
                console.error('refreshMyGallery failed', e);
            }
        }
        function onDelete(item) {
            deleteTarget.value = item;
            nextAction.value = 'delete';
            infoTitle.value = 'Delete Voice';
            infoMessage.value = 'Are you sure you want to delete this voice?';
            showInfoPopup.value = true;
        }
        async function onConfirm() {
            if (nextAction.value == 'delete') {
                try {
                    const response = await DeleteUserVoice(deleteTarget.value.voice_id);
                    if (response && response.error) {
                        infoTitle.value = 'Warning!';
                        infoMessage.value = response.error;
                        showInfoPopup.value = true;
                        showInfoPopupCancelButton.value = false;
                    } else {
                        deleteTarget.value = null;
                        nextAction.value = '';
                        loadVoices();
                    }
                } catch (err) {
                    infoTitle.value = 'Warning!';
                    infoMessage.value = (err && err.message) ? err.message : 'Failed to delete voice. Please try again.';
                    showInfoPopup.value = true;
                    showInfoPopupCancelButton.value = false;
                }
            }
        }
        function onModify(item) {
            editItem.value = item;
            console.log(item);
            editFields.value = [
                { key: 'voice_name', label: 'Voice Name', default: item.voice_name },
                { key: 'tag', label: 'Tags (comma-separated)', default: Array.isArray(item.tag) ? item.tag.join(', ') : item.tag || '' },
                { key: 'instruct_text', label: 'Instruction Text', default: item.instruct_text || '' }
            ];
            showEditForm.value = true;
        }

        async function onConfirmEdit(values) {
            if (!editItem.value) return;
            const voice_id = editItem.value.voice_id;
            const item = editItem.value;
            const oldName = item.voice_name;
            const oldTagArray = Array.isArray(item.tag)
                ? item.tag
                : (item.tag ? item.tag.split(',').map(t => t.trim()) : []);
            const oldInstruct = item.instruct_text || '';

            // new values from form
            const newName = values.voice_name.trim();
            const newTagArray = values.tag.split(',').map(t => t.trim());
            const newTagString = newTagArray.join(', ');
            const newInstruct = values.instruct_text.trim();

            const nameLengthLimit = 30;
            const checkName = checkInput(newName, nameLengthLimit);
            if (!checkName.isLongEnough) {
                nextAction.value = '';
                infoTitle.value = 'Warning!';
                infoMessage.value = `Name is too long (${newName.length} / ${nameLengthLimit} characters). Please shorten it.`;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }
            if (!checkName.hasOnlyAllowed) {
                nextAction.value = '';
                infoTitle.value = 'Warning!';
                infoMessage.value = "Name contains invalid characters: " + checkName.disallowedChars.join(' ') + ". Only English letters, numbers, and common punctuation are allowed.";
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }

            const tagLengthLimit = 30;
            const checkTag = checkInput(newTagString, tagLengthLimit);
            if (!checkTag.isLongEnough) {

                nextAction.value = '';
                infoTitle.value = 'Warning!';
                infoMessage.value = `Tag is too long (${newTagString.length} / ${tagLengthLimit} characters). Please shorten it.`;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }
            if (!checkTag.hasOnlyAllowed) {

                nextAction.value = '';
                infoTitle.value = 'Warning!';
                infoMessage.value = "Tag contains invalid characters: " + checkTag.disallowedChars.join(' ') + ". Only English letters, numbers, and common punctuation are allowed.";
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }

            const instructLengthLimit = 500;
            const checkInstruct = checkInput(newInstruct, instructLengthLimit);
            if (!checkInstruct.isLongEnough) {
                nextAction.value = '';
                infoTitle.value = 'Warning!';
                infoMessage.value = `Instruct text is too long (${newInstruct.length} / ${instructLengthLimit} characters). Please shorten it.`;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }
            if (!checkInstruct.hasOnlyAllowed) {
                nextAction.value = '';
                infoTitle.value = 'Warning!';
                infoMessage.value = "Instruct text contains invalid characters: " + checkInstruct.disallowedChars.join(' ') + ". Only English letters, numbers, and common punctuation are allowed.";
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }

            // determine which fields changed
            const changedFields = [];
            if (newName !== oldName) {
                changedFields.push(`voice_name: "${oldName}" → "${newName}"`);
                await patchUserVoiceName(voice_id, newName)
            }
            console.log(JSON.stringify(newTagArray))
            console.log(JSON.stringify(oldTagArray))
            if (JSON.stringify(newTagArray) !== JSON.stringify(oldTagArray)) {
                changedFields.push(
                    `tag: "${oldTagArray.join(', ')}" → "${newTagString}"`
                );
                await patchUserVoiceTag(voice_id, newTagString)
            }
            if (newInstruct !== oldInstruct) {
                changedFields.push(
                    `instruct_text: "${oldInstruct}" → "${newInstruct}"`
                );
                await patchUserVoiceInstructText(voice_id, newInstruct)
            }

            // log changes
            if (changedFields.length) {
                console.log('Modified fields:', changedFields.join('; '));
            } else {
                console.log('No changes made.');
            }

            // apply updates
            item.voice_name = newName;
            item.tag = newTagString;
            item.instruct_text = newInstruct;
            showEditForm.value = false;
        }
        function onCancelEdit() {
            showEditForm.value = false;
        }

        function onDropdownAction(item, action) {
            if (action === 'modify') {
                onModify(item);
            } else if (action === 'delete') {
                onDelete(item);
            }
        }

        onMounted(async () => {
            await loadVoices();
            try {
                const ids = await getCollectVoiceIds();
                myGalleryIds.value = Array.isArray(ids) ? ids : [];
            } catch (e) {
                console.error('Failed to load collected voice IDs', e);
            }
        });
        return {
            q,
            onlyMyGallery,
            myGalleryIds,
            filtered,
            searchFields,
            itemKey,
            displayName,
            displayInstrctText,
            displayTags,
            play,
            select,
            toggleMyGallery,
            isInGallery,
            voiceKey,
            loadingVoices,
            isLoading,
            voiceImageUrls,
            showInfoPopup,
            infoTitle,
            infoMessage,
            currentAudio,
            currentPlayingId,
            isPlaying,
            stop,
            onDelete,
            onModify,
            deleteTarget,
            onConfirm,
            nextAction,
            showEditForm,
            editFields,
            onConfirmEdit,
            onCancelEdit,
            onDropdownAction,
            audioProgress,
            showInfoPopupCancelButton
        };
    }
});
</script>

<style scoped>
.pm-modal {
    background: #fff;
    width: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    max-width: 100%;
    max-height: 100%;
}

.pm-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: .75rem 1rem;
    border-bottom: 1px solid var(--border-color);
}

.pm-search {
    margin: .75rem 1rem;
    padding: .5rem .75rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    width: calc(100% - 2rem);
}


.pm-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.2rem;
    padding: 1rem;
    overflow-y: auto;
    align-content: start;
}

.pm-item {
    padding: .6rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    cursor: pointer;
    transition: background .15s;
    overflow: hidden;
}

.pm-item:hover {
    background: #f5f5f5;
}

.pm-empty {
    padding: 1rem;
    text-align: center;
    color: #888;
}


.vc-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.5rem;
}

.vc-btn-group {
    position: absolute;
    top: 0.6rem;
    right: 0.6rem;
    display: flex;
    gap: 0.4rem;
}

.vc-name-tags h3 {
    font-size: 1.1rem;
}

.vc-name-row {
    display: flex;
    flex-direction: row;
    margin-bottom: 5px;
}

.vc-card {
    position: relative;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: .3rem;
    padding: 0.6rem 0.6rem 0.6rem 1.2rem;
    height: 100%;
    overflow: visible;
    z-index: 1;
}

.vc-card:hover {
    background-color: #eff9ff;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
}

.vc-card-progress {
    pointer-events: none;
    position: absolute;
    top: -4px;
    left: -4px;
    width: 72px;
    height: 72px;
    z-index: 2;
    border-radius: 50%;
    /* Circular progress ring using conic-gradient */
    background: conic-gradient(var(--purple, #8800ff) calc(var(--progress, 0) * 100%),
            #e0e0e0 0);
    opacity: 0.7;
}

.vc-card-progress::after {
    content: "";
    position: absolute;
    top: 8px;
    left: 8px;
    width: 56px;
    height: 56px;
    background: #fff;
    border-radius: 50%;
    z-index: 3;
    pointer-events: none;
}

.vc-tags {
    font-size: 0.8rem;
    color: var(--purple);
    font-weight: 500;
}

.vc-fav-btn {
    border: none;
    background: #f9f4ff;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    line-height: 32px;
    text-align: center;
    cursor: pointer;
    font-size: 16px;
    color: #ff4a6a;
    flex-shrink: 0;
    transition: background .15s;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: none;
}

.vc-fav-btn:hover {
    background: #ffdfe5;
}

.vc-fav-btn.active {
    background: #f9f4ff;
}

.vc-fav-btn svg {
    display: block;
}


.vc-modify-btn {
    border: none;
    background: #e6ffed;
    color: #2f855a;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    line-height: 32px;
    text-align: center;
    cursor: pointer;
    font-size: 16px;
    flex-shrink: 0;
    transition: background .15s;
}

.vc-modify-btn:hover {
    background: #c6f6d5;
}

.vc-delete-btn {
    border: none;
    background: #ffe6e6;
    color: #e53e3e;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    line-height: 32px;
    text-align: center;
    cursor: pointer;
    font-size: 16px;
    flex-shrink: 0;
    transition: background .15s;
}

.vc-delete-btn:hover {
    background: #feb2b2;
}

.pm-gallery-toggle {
    padding: 0 1rem 0.75rem 1rem;
    display: flex;
    justify-content: flex-end;
    box-sizing: border-box;
}

.pm-gallery-toggle .switch {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    gap: 0.5rem;
    user-select: none;
    font-size: 0.9rem;
    color: #1a2b4d;
}

.pm-gallery-toggle .switch input {
    display: none;
}

.pm-gallery-toggle .switch .slider {
    position: relative;
    width: 44px;
    height: 24px;
    background: #d1d5db;
    border-radius: 12px;
    transition: background .2s;
    flex-shrink: 0;
}

.pm-gallery-toggle .switch .slider::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    background: #fff;
    border-radius: 50%;
    transition: transform .2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
}

.pm-gallery-toggle .switch input:checked+.slider {
    background: var(--purple-hover);
}

.pm-gallery-toggle .switch input:checked+.slider::after {
    transform: translateX(20px);
}

.pm-gallery-toggle .switch-label {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-weight: 500;
    color: #4a4a6a;
}


@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.vc-avatar {
    position: relative;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin-right: 12px;
    /* overflow: hidden; */
    flex-shrink: 0;
    background: #f0f0f0;
    cursor: pointer;
    transition: transform 0.2s, opacity 0.2s;
}

.vc-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s;
}

.vc-avatar.disabled {
    pointer-events: none;
    opacity: 0.6;
}

.vc-avatar:hover:not(.disabled) img {
    transform: scale(1.05);
}

.vc-avatar .spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-width: 4px;
    border-style: solid;
    border-color: #4a61ff transparent transparent transparent;
    animation: spin 0.6s linear infinite;
    border-radius: 50%;
}

.vc-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.vc-designed-badge {
    display: inline-block;
    margin-right: 1rem;
    padding: 0.2rem 0.5rem;
    border: 1px solid var(--purple);
    border-radius: 4px;
    font-size: 0.75rem;
    color: var(--purple);
}

.vc-play-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: var(--purple);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.vc-instruct {
    font-size: 0.95rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
    line-height: 1.3;
}

.vc-more-btn {
    border: none;
    background: #f4f4f7;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    padding: 0;
}

.vc-more-btn:hover,
.vc-more-btn:focus {
    background: #ececff;
    box-shadow: 0 2px 8px rgba(136, 0, 255, 0.08);
    outline: none;
}

.vc-more-btn svg {
    display: block;
}

.pm-modal {
    background: transparent;
    gap: 0.85rem;
    padding: 0 1.25rem 1.25rem;
}

.pm-header {
    border-bottom: 0;
    padding: 0.25rem 0 0;
}

.pm-eyebrow {
    color: var(--purple);
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1;
    margin-bottom: 0.35rem;
    text-transform: uppercase;
}

.pm-header h3 {
    color: #0f172a;
    font-size: 1.15rem;
}

.pm-count {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    color: #475569;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.4rem 0.7rem;
}

.pm-search-wrap {
    align-items: center;
    background: #ffffff;
    border: 1px solid #dbe3ee;
    border-radius: 14px;
    display: flex;
    gap: 0.65rem;
    padding: 0 0.9rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    width: 100%;
}

.pm-search-wrap:focus-within {
    border-color: rgba(136, 0, 255, 0.45);
    box-shadow: 0 0 0 3px rgba(136, 0, 255, 0.08);
}

.pm-search-wrap i {
    color: #94a3b8;
}

.pm-search {
    border: 0;
    border-radius: 0;
    color: #0f172a;
    margin: 0;
    padding: 0.85rem 0;
    width: 100%;
}

.pm-search:focus {
    outline: none;
}

.pm-gallery-toggle {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 0.75rem;
}

.pm-list {
    gap: 0.85rem;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    padding: 0;
}

.pm-item {
    padding: 0;
}

.vc-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.04);
    min-height: 150px;
    padding: 1rem;
}

.vc-card:hover {
    background: #ffffff;
    border-color: #dbe3ee;
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.08);
    transform: translateY(-2px);
}

.vc-card:focus {
    outline: none;
}

.vc-card:focus-visible {
    border-color: #dbe3ee;
    box-shadow: 0 0 0 3px rgba(136, 0, 255, 0.14), 0 18px 34px rgba(15, 23, 42, 0.08);
    outline: none;
}

.vc-card.playing {
    background: linear-gradient(135deg, #ffffff, #fbf7ff);
}

.vc-avatar {
    background: #eef2ff;
    border-radius: 18px;
    height: 72px;
    margin-right: 1rem;
    overflow: hidden;
    width: 72px;
}

.vc-play-btn {
    background: var(--purple);
    border-radius: 999px;
    color: #ffffff;
    font-size: 1rem;
    transition: background 0.2s ease, transform 0.2s ease;
}

.vc-play-btn:hover,
.vc-play-btn:focus {
    background: var(--purple-hover);
    outline: none;
    transform: scale(1.04);
}

.vc-card-progress {
    height: 80px;
    left: -4px;
    top: -4px;
    width: 80px;
}

.vc-card-progress::after {
    height: 62px;
    width: 62px;
}

.vc-content {
    min-width: 0;
}

.vc-name-row {
    align-items: center;
    flex-wrap: wrap;
    gap: 0.55rem;
    justify-content: flex-start;
    margin-bottom: 0.35rem;
    text-align: left;
}

.vc-name-tags {
    text-align: left;
}

.vc-name-tags h3 {
    color: #0f172a;
    font-size: 1.1rem;
    line-height: 1.2;
    text-align: left;
}

.vc-designed-badge {
    background: #faf5ff;
    border: 1px solid rgba(136, 0, 255, 0.2);
    border-radius: 999px;
    font-weight: 700;
    margin-right: 0;
}

.vc-tags {
    color: #6b21a8;
    font-size: 0.82rem;
}

.vc-instruct {
    color: #475569;
    display: -webkit-box;
    line-height: 1.35;
    margin: 0.45rem 0 0;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

.vc-fav-btn,
.vc-more-btn {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #94a3b8;
}

.vc-fav-btn:hover,
.vc-fav-btn.active {
    background: #fff1f2;
    border-color: #fecdd3;
    color: #e11d48;
}

.vc-more-btn:hover,
.vc-more-btn:focus {
    background: #f5f3ff;
    border-color: rgba(136, 0, 255, 0.18);
    color: var(--purple);
}

.pm-empty {
    align-items: center;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 16px;
    color: #64748b;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    grid-column: 1 / -1;
    padding: 2rem;
}

.pm-empty i {
    color: var(--purple);
    font-size: 1.4rem;
}

.pm-empty strong {
    color: #0f172a;
}

:deep(.el-dropdown-menu__item) {
    align-items: center;
    display: flex;
    gap: 0.5rem;
}

@media (max-width: 760px) {
    .pm-modal {
        padding: 0 0.85rem 0.85rem;
    }

    .pm-header {
        align-items: stretch;
        flex-direction: column;
    }

    .vc-card {
        min-height: 160px;
    }
}
</style>
